import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { defer, forEach, isNil } from 'lodash';
import tinyColor from 'tinycolor2';
import { RouteFromQuery } from '@mews-distributor/common';
import { BookingEngine, ConfigurationResult } from '@mews-distributor/data-access';

import { SENTRY_ERROR_FILTERS, TrackingConsents } from '@mews/framework';
import { DateTimeUtils, Time, WallClockDate } from '@mews/framework/datetime';
import { BadRequestError, BusinessError } from '@mews/framework/errors';

import { createAction } from '@mews/framework/redux';
import { setDates, setEndTimeUtc, setStartTimeUtc } from '../../actions/dates';
import { initializeAvailabilityData, setInitialDates } from '../../actions/init';
import { showConfirmation, showDates, showProperties, showSpaceCategories } from '../../actions/navigation';
import { getConfiguration } from '../../api/actions/configuration';
import { ignoredErrors, logSentryError } from '../../sentry/sentry';
import { FLEXIBLE_RATES_FILTER_CHANGED } from '../../middlewares/gtmActions';
import { shouldGetSelectedCityPricing } from '../../selectors/cityPricing';
import { hasSelectedEnterpriseData } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { setMultiPropertyToolingData } from '../../services/featureFlagService/actions';
import { setInitialGainsightServiceGlobalContext, setGainsightUser } from '../../services/gainsightService/gainsight';
import { trackEvent } from '../../services/logging/loggingHelpers';
import { prepareTrackingConsents } from '../../services/trackingConsents/actions';
import { TRACKING_CONSENTS_SET } from '../../services/trackingConsents/actionTypes';
import { generateTrackingConsents } from '../../services/trackingConsents/generateTrackingConsents';
import {
    ApplicationState,
    DistributorPromiseThunkAction,
    DistributorThunkAction,
    ThunkExtraArgument,
} from '../../types';
import { hasCheckoutPreviousStep } from '../../views/confirmation/data/selectors';
import { initializeDatesOccupancies } from '../../views/dates/actions';
import { calendarDatesChanged, timesAreUserSelected } from '../../views/dates/slices/calendar';
import {
    APP_CLOSED,
    APP_INITIALIZATION_FAILED,
    APP_INITIALIZATION_STARTED,
    APP_INITIALIZATION_SUCCEEDED,
    APP_OPENED,
} from '../app/actionTypes';
import {
    areDeeplinkDatesSet,
    getAdultCountFromQuery,
    getChildCountFromQuery,
    getCityIdFromQuery,
    getCurrentRoute,
    getHotelFromQuery,
    getLanguageCode,
    getPropertyReturnUrlFromQuery,
    getReservationGroupIdFromQuery,
    getRoomFromQuery,
    getRouteFromQuery,
    getSelectedCityId,
    getVoucherCodeFromQuery,
    isAppInitialized,
    isFlexibleRatesFilterEnabledQuery,
} from '../app/selectors';
import { isClosable } from '../app/viewSelectors';
import { shouldGetSelectedCityAvailability } from '../availability/cityAvailability';
import { CONFIGURATION_SET, PRIMARY_COLOR_SET, THEME_VARIANT_SET } from '../configuration/actionTypes';
import { getConfigurationIds, getHotelIds as getHotelIdsSelector, isMultiHotel } from '../configuration/selectors';
import { hasAvailabilityBlocksFailed } from '../entities/availabilityBlocks/selectors';
import {
    getEnterpriseCityId,
    getEnterpriseTimezone,
    getFirstEnterpriseCityId,
    getFirstEnterpriseId,
} from '../entities/enterprises/selectors';
import { fetchCurrentDatesAvailabilityWithSelectedCurrencyCode } from '../entities/services/actions';
import { setInitialAdultCount, setInitialChildCount } from '../occupancy/reducer';
import { isValidOccupancyCount } from '../occupancy/utils';
import { OTHER_DATES_SELECTED } from '../otherDatesSuggestions/actionTypes';
import {
    selectCity,
    selectHotel,
    setEnterprise,
    setVoucherCode,
    trySelectCity,
    trySelectHotel,
    trySelectRoom,
} from '../resourceOrder/actions';
import { getSelectedHotelId } from '../resourceOrder/selectors';
import { ThemeVariant } from '../../components/service/ThemeWrapper';
import { BOOKING_RESTARTED, GTM_CONFIGURATION_SET } from './actionTypes';
import { initializeAssetLoaders } from './assetLoader';
import { setConfiguredCurrencyCode, setDetectedCurrency } from './currencyCode';
import { checkLanguageConfiguration, setConfiguredLanguageCode } from './languageCode';
import {
    getBooleanField,
    getConfigurationEnterprise,
    getConfigurationOccupancy,
    getField,
    getHotelIds,
    getNumberField,
    getPrimaryConfiguration,
} from './processConfiguration';

function awaitFeatureFlagServiceInitialization(): DistributorPromiseThunkAction {
    return async (_dispatch, _getState, { loggerService, featureFlagService }) => {
        try {
            await featureFlagService.initialize();
        } catch (error) {
            const { message, constructor } = error;
            const { name } = constructor;
            loggerService.log('featureFlagService', 'Initialization failed.', {
                errorMessage: message,
                errorInstance: name,
            });
        }
    };
}

const setInitialOccupancyFromQuery = (): DistributorThunkAction => (dispatch, getState) => {
    const adultCountFromQuery = getAdultCountFromQuery(getState());
    const childCountFromQuery = getChildCountFromQuery(getState());

    if (isValidOccupancyCount(adultCountFromQuery)) {
        dispatch(setInitialAdultCount(adultCountFromQuery));
    }

    if (isValidOccupancyCount(childCountFromQuery)) {
        dispatch(setInitialChildCount(childCountFromQuery));
    }
};
const ignoreErrorList = [...ignoredErrors, ...SENTRY_ERROR_FILTERS];
const isErrorIgnored = (error?: Error) =>
    pipe(
        ignoreErrorList,
        array.some((regExp) => regExp.test(`${error?.name}: ${error?.message}`))
    );

function initApplication(): DistributorPromiseThunkAction {
    return async (dispatch, getState: () => ApplicationState, { loggerService }: ThunkExtraArgument) => {
        dispatch(createAction(APP_INITIALIZATION_STARTED));

        await dispatch(awaitFeatureFlagServiceInitialization());

        void dispatch(prepareTrackingConsents());
        dispatch(setInitialOccupancyFromQuery());

        const configurationPromise = dispatch(getConfiguration())
            .then((configuration: ConfigurationResult) => {
                const cityIdFromQuery = getCityIdFromQuery(getState());

                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                if (cityIdFromQuery) {
                    dispatch(trySelectCity(cityIdFromQuery));
                }

                return dispatch(processConfiguration(configuration));
            })
            .then(
                async (configuration: {
                    configurationResult: ConfigurationResult;
                    bookingEngineConfiguration: BookingEngine;
                }) => {
                    if (configuration == null) {
                        throw new Error('Configuration is null');
                    }

                    const multiHotel = isMultiHotel(getState());
                    // select initial city
                    const selectedCityId = getSelectedCityId(getState());
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (!selectedCityId) {
                        const cityId = getFirstEnterpriseCityId(getState());
                        void dispatch(selectCity(cityId));
                    }

                    // If not multihotel, the default hotel is always selected
                    if (!multiHotel && !hasSelectedEnterpriseData(getState(), {})) {
                        const enterpriseId = getFirstEnterpriseId(getState());
                        await dispatch(selectHotel(enterpriseId));
                    }

                    dispatch(setConfiguredLanguageCode(configuration));

                    await dispatch(initializeAvailabilityData());
                    // Set the detected currency here for now as it relies on a feature flag that needs the enterprise id
                    await dispatch(setDetectedCurrency(configuration.bookingEngineConfiguration.id));
                }
            )
            .then(() => {
                dispatch(initializeDatesOccupancies());
            })
            .then(() => {
                // This deffers the execution to the end of promise chain
                defer(async () => {
                    if (hasAvailabilityBlocksFailed(getState())) {
                        dispatch(showDates());
                        return;
                    }

                    const routeFromQuery = getRouteFromQuery(getState());
                    const roomFromQuery = getRoomFromQuery(getState());
                    const hotelFromQuery = getHotelFromQuery(getState());
                    const isFlexibleRatesFilter = isFlexibleRatesFilterEnabledQuery(getState());
                    const reservationGroupIdFromQuery = getReservationGroupIdFromQuery(getState());

                    const isDatesDeeplinkSet = areDeeplinkDatesSet(getState());
                    if (isDatesDeeplinkSet) {
                        dispatch(calendarDatesChanged());
                    }

                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (isFlexibleRatesFilter) {
                        dispatch(
                            createAction(FLEXIBLE_RATES_FILTER_CHANGED, {
                                changedByUser: false,
                                state: true,
                            })
                        );
                    }

                    if (routeFromQuery === RouteFromQuery.SpaceCategories) {
                        void dispatch(showSpaceCategories());
                    }

                    const multiHotel = isMultiHotel(getState());

                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (!multiHotel && routeFromQuery === RouteFromQuery.Rates && roomFromQuery) {
                        void dispatch(trySelectRoom(roomFromQuery));
                    }

                    if (multiHotel) {
                        if (routeFromQuery === RouteFromQuery.Properties) {
                            void dispatch(showProperties());
                        }

                        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                        if (routeFromQuery === RouteFromQuery.SpaceCategories && hotelFromQuery) {
                            await dispatch(trySelectHotel(hotelFromQuery));
                        }

                        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                        if (routeFromQuery === RouteFromQuery.Rates && hotelFromQuery && roomFromQuery) {
                            const enterpriseCityId = getEnterpriseCityId(getState(), { enterpriseId: hotelFromQuery });
                            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                            if (enterpriseCityId) {
                                dispatch(trySelectCity(enterpriseCityId));
                            }

                            await dispatch(trySelectHotel(hotelFromQuery));
                            void dispatch(trySelectRoom(roomFromQuery));
                        }
                    }

                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (reservationGroupIdFromQuery) {
                        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                        if (hotelFromQuery) {
                            dispatch(setEnterprise(hotelFromQuery));
                        }
                        dispatch(showConfirmation());
                    } else if (isNil(getCurrentRoute(getState()))) {
                        dispatch(showDates());
                    }
                });
            });

        try {
            await Promise.all([configurationPromise, dispatch(initializeAssetLoaders())]);

            dispatch(createAction(APP_INITIALIZATION_SUCCEEDED));
            dispatch(setGainsightUser());
            dispatch(setInitialGainsightServiceGlobalContext());
        } catch (error) {
            dispatch(createAction(APP_INITIALIZATION_FAILED));

            const state = getState();
            const configurationIds = getConfigurationIds(state);
            const hotelIds = getHotelIdsSelector(state);
            if (isErrorIgnored(error)) {
                return;
            }
            /* eslint-disable @typescript-eslint/prefer-regexp-exec */
            if (
                (error instanceof BusinessError || error instanceof BadRequestError) &&
                error.message.match(/^Invalid JSON\.$/)
            ) {
                loggerService.log(
                    'distributor-invalid-configuration-ids',
                    `Distributor used invalid configuration ids.`,
                    {
                        error,
                        configurationIds,
                        hotelIds,
                    }
                );
                return;
            }

            dispatch(
                logSentryError({
                    error,
                    extra: {
                        step: 'INIT_APP_CHAIN',
                        configurationIds,
                        hotelIds,
                    },
                })
            );
        }
    };
}

export function setConfiguration(configuration: $TSFixMe) {
    return createAction(CONFIGURATION_SET, configuration);
}

interface SetDatesPayload {
    startDate: WallClockDate;
    endDate: WallClockDate;
    label: string | undefined;
    startTime?: Time;
    endTime?: Time;
}

export const selectOtherDates =
    ({ startDate, endDate, label, startTime, endTime }: SetDatesPayload): DistributorThunkAction =>
    (dispatch, getState) => {
        dispatch(createAction(OTHER_DATES_SELECTED, { label }));
        const state = getState();
        const enterpriseId = getSelectedHotelId(state);
        const timezone = getEnterpriseTimezone(state, { enterpriseId });

        // TODO: remove when other date suggestion bypassed for hourly
        if (timezone != null && startTime != null && endTime != null) {
            const startTimeUtc = DateTimeUtils.createDateTimeUtc({ date: startDate, time: startTime, timezone });
            const endTimeUtc = DateTimeUtils.createDateTimeUtc({ date: endDate, time: endTime, timezone });
            dispatch(setStartTimeUtc(startTimeUtc, timezone));
            dispatch(setEndTimeUtc(endTimeUtc, timezone));
            dispatch(timesAreUserSelected(true));
        }

        dispatch(setDates(startDate.value, endDate.value));

        if (shouldGetSelectedCityAvailability(state, {}) || shouldGetSelectedCityPricing(state)) {
            void dispatch(fetchCurrentDatesAvailabilityWithSelectedCurrencyCode());
        }
    };

function setPrimaryColor(primaryColor: string): DistributorThunkAction {
    return (dispatch) => {
        const color = tinyColor(primaryColor);
        const themeVariant: ThemeVariant = color.isLight() ? 'Light' : 'Dark';

        dispatch(setThemeVariant(themeVariant));
        dispatch(createAction(PRIMARY_COLOR_SET, { primaryColor }));
    };
}

function setThemeVariant(variant: ThemeVariant) {
    return createAction(THEME_VARIANT_SET, { variant });
}

export const handleConfirmationBackAction =
    (): DistributorThunkAction =>
    (dispatch, getState, { historyService }) => {
        if (hasCheckoutPreviousStep(getState())) {
            return dispatch(restartBooking());
        }
        historyService.goBack();
    };

export const restartBooking =
    (): DistributorThunkAction =>
    (dispatch, _getState, { gainsightService }) => {
        dispatch(showDates());
        dispatch(createAction(BOOKING_RESTARTED));
        gainsightService.reset();
    };

export function openApp(): DistributorPromiseThunkAction {
    return async (dispatch, getState, { historyService }) => {
        dispatch(createAction(APP_OPENED));
        historyService.startTracking();

        if (!isAppInitialized(getState())) {
            await dispatch(initApplication());
        }
    };
}

export function closeApp(): DistributorThunkAction {
    return (dispatch, getState, { historyService }) => {
        const state = getState();
        if (isClosable(state)) {
            historyService.stopTracking();

            const propertyUrl = getPropertyReturnUrlFromQuery(state);

            dispatch(createAction(APP_CLOSED, { propertyUrl }));
        } else {
            historyService.handleLeaving();
        }
    };
}

function processConfiguration(configurationResult: ConfigurationResult): DistributorThunkAction<{
    configurationResult: ConfigurationResult;
    bookingEngineConfiguration: BookingEngine;
}> {
    return (dispatch, getState) => {
        const state = getState();

        const configurationOption = getPrimaryConfiguration(configurationResult.bookingEngines);

        if (option.isNone(configurationOption)) {
            throw new Error('No primary configuration found');
        }
        const bookingEngineConfiguration = configurationOption.value;

        const getEnterprise = getConfigurationEnterprise(configurationResult);
        const enterpriseIds = pipe(
            configurationResult.enterprises,
            array.map((enterprise) => enterprise.id)
        );
        void dispatch(setMultiPropertyToolingData(enterpriseIds));
        const selectedCityId = getSelectedCityId(state);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!selectedCityId) {
            const cityId = pipe(
                bookingEngineConfiguration,
                getEnterprise,
                option.map((enterprise) => enterprise.cityId),
                option.toNullable
            );

            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (cityId) {
                // HACK: avoid reading problematic data in initial state, before they're checked
                void dispatch(selectCity(cityId, { checkStartDate: false }));
            }
        }

        dispatch(setConfiguredCurrencyCode({ bookingEngineConfiguration, configurationResult }));

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const voucherCode = getVoucherCodeFromQuery(state) || getField(bookingEngineConfiguration, 'voucherCode');
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (voucherCode) {
            dispatch(trackEvent('VoucherCodePreffiled'));
            dispatch(setVoucherCode(voucherCode));
        }

        const getOccupancy = getConfigurationOccupancy(configurationResult);
        const hotels = {};
        forEach(configurationResult.bookingEngines, (hotelConfiguration) => {
            const features = {
                rateCompare: {
                    isEnabled: getBooleanField(hotelConfiguration, 'displayRateComparison'),
                    competitors: getField(hotelConfiguration, 'onlineTravelAgencies'),
                    competitorPriceRelativeAdjustment: getNumberField(
                        hotelConfiguration,
                        'competitorPriceRelativeAdjustment'
                    ),
                    rateDescription: getField(hotelConfiguration, 'competitorRateDescription'),
                },
                specialRequestsField: getBooleanField(hotelConfiguration, 'displaySpecialRequests'),
                enableChildSelection: getBooleanField(hotelConfiguration, 'childSelectionEnabled'),
                displayAvailability: getBooleanField(hotelConfiguration, 'displayAvailability'),
                displayFlexibleRates: getBooleanField(hotelConfiguration, 'displayFlexibleRates'),
            };

            const occupancy = pipe(hotelConfiguration.occupancyData, getOccupancy);
            const settings = {
                bookerReservationCustomerEmailInput: getField(
                    hotelConfiguration,
                    'bookerReservationCustomerEmailInput'
                ),
                checkoutStatement: getField(hotelConfiguration, 'checkoutStatement'),
                paymentCardRequirement: getField(hotelConfiguration, 'paymentCardRequirement'),
                configurationId: hotelConfiguration.id,
                serviceId: hotelConfiguration.serviceId,
                defaultAdultCount: getNumberField(occupancy, 'adultCount'),
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                defaultChildCount: features.enableChildSelection ? getNumberField(occupancy, 'childCount') : 0,
                maxAdultCount: getNumberField(hotelConfiguration, 'maxAdultCount'),
                requiredFields: getField(hotelConfiguration, 'requiredFields'),
                priceLabel: getField(hotelConfiguration, 'taxMessage'),
            };

            const enterpriseId = pipe(
                hotelConfiguration,
                getEnterprise,
                option.map((enterprise) => enterprise.id)
            );

            if (option.isNone(enterpriseId)) {
                throw new Error('Could not find enterprise for hotel configuration');
            }
            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
            hotels[enterpriseId.value] = { features, settings };
        });

        const settings = {
            startDateOffset: getNumberField(bookingEngineConfiguration, 'startDateOffset'),
            endDateOffset: getNumberField(bookingEngineConfiguration, 'endDateOffset'),
            hotelIds: getHotelIds(configurationResult.enterprises),
            introVideoSrc: getField(bookingEngineConfiguration, 'introVideoUrl'),
            googleTagManagerContainerId: getField(bookingEngineConfiguration, 'gtmContainerId'),
            displayVoucherCode: getBooleanField(bookingEngineConfiguration, 'displayVoucherCode'),
            cancellationOffset: getField(bookingEngineConfiguration, 'cancellationOffset'),
            includeEmailInPurchaseEvent: getBooleanField(bookingEngineConfiguration, 'includeEmailInPurchaseEvent'),
        };

        const primaryColor = getField(bookingEngineConfiguration, 'primaryColor');
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (primaryColor) {
            dispatch(setPrimaryColor(primaryColor));
        }

        const themeVariant = getField(bookingEngineConfiguration, 'theme');
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (themeVariant) {
            dispatch(setThemeVariant(themeVariant));
        }

        dispatch(setConfiguration({ settings, hotels }));
        dispatch(createAction(GTM_CONFIGURATION_SET));

        dispatch(checkLanguageConfiguration(getLanguageCode(getState())));
        dispatch(setInitialDates({ shouldSetNonDisabledDates: false, minimalStartDate: null, maximalEndDate: null }));

        return { configurationResult, bookingEngineConfiguration };
    };
}

const setTrackingConsents =
    (consents: TrackingConsents): DistributorThunkAction =>
    (dispatch, _getState, { trackingConsentsService }) => {
        trackingConsentsService.setDefaultConsents(consents);

        return dispatch(
            createAction(TRACKING_CONSENTS_SET, {
                trackingConsents: consents,
            })
        );
    };

/* ConfigurationIds will be an empty array in most calls, given that these methods are called before the application starts. */

export const disableTracking =
    (): DistributorThunkAction =>
    (dispatch, getState, { loggerService }) => {
        const configurationIds = getConfigurationIds(getState());
        loggerService.log('disableTracking', 'Manually disabled tracking for widget via widget API', {
            configurationIds,
        });
        return dispatch(setTrackingConsents(generateTrackingConsents(false)));
    };

export const enableTracking =
    (): DistributorThunkAction =>
    (dispatch, getState, { loggerService }) => {
        const configurationIds = getConfigurationIds(getState());
        loggerService.log('enableTracking', 'Manually enabled tracking for widget via widget API', {
            configurationIds,
        });
        return dispatch(setTrackingConsents(generateTrackingConsents(true)));
    };
